import { useCallback } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { AskMessage } from '@/components/common/Ask/Ask';
import type { AskSessionMessage, AskSessionSession } from '@/apis/ask/askHistory/types';
import { AskRelayHubEvents, WorkspaceRelayHubEvents } from '@/handlers/signalR';

import { transformApiMessageToComponent } from './AskSession.utils';

type AskSessionSignalRHandlersParams = {
  currentUserId: string;
  sessionId?: string;

  // Ask session streaming state
  setMessages: Dispatch<SetStateAction<AskMessage[]>>;
  setProcessingStatus: Dispatch<SetStateAction<string | null>>;
  setAskSession: Dispatch<SetStateAction<AskSessionSession | null>>;
  setStreamingMessageId: Dispatch<SetStateAction<string | null>>;

  lastStreamContentRef: MutableRefObject<string>;
  displayedContentRef: MutableRefObject<string>;
  toRevealRef: MutableRefObject<string>;

  // Workspace upload state
  uploadingFileNames: string[];
  setUploadingFileNames: Dispatch<SetStateAction<string[]>>;
  normalizeFileName: (name: string | undefined | null) => string;

  // Data refresh callbacks
  getWorkspaceData: () => Promise<void>;
  fetchAskSessionData: () => Promise<void>;
};

export const useAskSessionSignalRHandlers = (
  params: AskSessionSignalRHandlersParams,
) => {
  const {
    currentUserId,
    sessionId,

    setMessages,
    setProcessingStatus,
    setAskSession,
    setStreamingMessageId,

    lastStreamContentRef,
    displayedContentRef,
    toRevealRef,

    uploadingFileNames,
    setUploadingFileNames,
    normalizeFileName,

    getWorkspaceData,
    fetchAskSessionData,
  } = params;

  const handleAskEventReceived = useCallback(
    (
      event: string,
      data: unknown,
      parsed?: Record<string, unknown>,
    ) => {
      const dataRecord =
        data && typeof data === 'object'
          ? (data as Record<string, unknown>)
          : {};
      const parsedRecord: Record<string, unknown> = parsed ?? {};

      const getStatusFromEventData = (defaultStatus: string): string => {
        const param2 = dataRecord.param2;
        if (typeof param2 === 'string' && param2.trim()) return param2;

        const param1 = dataRecord.param1;
        if (Array.isArray(param1) && typeof param1[1] === 'string') {
          return param1[1];
        }

        return defaultStatus;
      };

      const isAskSessionMessage = (
        value: unknown,
      ): value is AskSessionMessage => {
        return (
          typeof value === 'object' &&
          value !== null &&
          'id' in value &&
          'content' in value &&
          'timestamp' in value
        );
      };

      // Handle ASK_SESSION_MESSAGE_RECIEVED - user message echoed from server; show status
      if (event === AskRelayHubEvents.ASK_SESSION_MESSAGE_RECIEVED) {
        const statusText = getStatusFromEventData(
          'Message received. Fetching response...',
        );
        setProcessingStatus(statusText);

        const messageData = parsedRecord.messageData;
        if (isAskSessionMessage(messageData)) {
          const transformedMessage = transformApiMessageToComponent(
            messageData,
            currentUserId,
          );

          setMessages((prev) => {
            const exists = prev.some((msg) => msg.id === transformedMessage.id);
            if (exists) return prev;

            // Replace optimistic user message with server message (same content) so we keep real id.
            const lastUser = [...prev].reverse().find(
              (m) => m.senderId === 'currentUser',
            );
            if (lastUser && lastUser.content === transformedMessage.content) {
              return prev.map((m) => (m.id === lastUser.id ? transformedMessage : m));
            }

            return [...prev, transformedMessage];
          });
        }
      }

      // Status events (param2/param1[1] from hub payload)
      if (event === AskRelayHubEvents.ASK_SESSION_MESSAGE_AUDIENCE) {
        setProcessingStatus(getStatusFromEventData('Determining audience in Ask.'));
      }
      if (event === AskRelayHubEvents.ASK_SESSION_MESSAGE_INTENT) {
        setProcessingStatus(getStatusFromEventData('Determining users intent in Ask.'));
      }
      if (event === AskRelayHubEvents.ASK_SESSION_MESSAGE_DOCUMENT_CONTEXT) {
        setProcessingStatus(
          getStatusFromEventData('Understanding document context in Ask.'),
        );
      }
      if (event === AskRelayHubEvents.ASK_SESSION_MESSAGE_DOCUMENTS) {
        setProcessingStatus(
          getStatusFromEventData('Determining available documents in Ask.'),
        );
      }

      // STREAM_STARTED: new message—reset stream refs so we start fresh for this message.
      if (event === AskRelayHubEvents.ASK_SESSION_MESSAGE_STREAM_STARTED) {
        const messageId =
          typeof parsedRecord.messageId === 'string'
            ? parsedRecord.messageId
            : undefined;
        const askSessionId =
          typeof parsedRecord.askSessionId === 'string'
            ? parsedRecord.askSessionId
            : '';

        if (messageId) {
          lastStreamContentRef.current = '';
          displayedContentRef.current = '';
          toRevealRef.current = '';
          setStreamingMessageId(messageId);

          setMessages((prev) => {
            if (prev.some((msg) => msg.id === messageId)) return prev;
            const placeholderOnly: AskSessionMessage = {
              id: messageId,
              _etag: '',
              userId: 'assistant',
              userName: 'Assistant',
              askSessionId: askSessionId,
              content: '',
              timestamp: new Date().toISOString(),
              role: 'Assistant',
              functionsUsed: [],
              prompt: '',
              citations: null,
              type: '0',
              userFeedback: '',
              citationsUsed: {},
              stepIndex: null,
              userInputValidated: null,
              userInput: null,
            };

            return [...prev, transformApiMessageToComponent(placeholderOnly, currentUserId)];
          });
        }
      }

      // STREAM: cumulative content.
      if (event === AskRelayHubEvents.ASK_SESSION_MESSAGE_STREAM) {
        const messageId =
          typeof parsedRecord.messageId === 'string'
            ? parsedRecord.messageId
            : undefined;
        const askSessionId =
          typeof parsedRecord.askSessionId === 'string'
            ? parsedRecord.askSessionId
            : '';
        const streamContent = parsedRecord.streamContent;

        if (messageId && streamContent !== undefined) {
          const newFull = String(streamContent ?? '');
          const previousStream = lastStreamContentRef.current;
          const delta = newFull.replace(previousStream, '');
          lastStreamContentRef.current = newFull;
          toRevealRef.current += delta;

          setStreamingMessageId(messageId);
          setMessages((prev) => {
            if (prev.some((msg) => msg.id === messageId)) return prev;
            const placeholderOnly: AskSessionMessage = {
              id: messageId,
              _etag: '',
              userId: 'assistant',
              userName: 'Assistant',
              askSessionId: askSessionId,
              content: '',
              timestamp: new Date().toISOString(),
              role: 'Assistant',
              functionsUsed: [],
              prompt: '',
              citations: null,
              type: '0',
              userFeedback: '',
              citationsUsed: {},
              stepIndex: null,
              userInputValidated: null,
              userInput: null,
            };

            return [...prev, transformApiMessageToComponent(placeholderOnly, currentUserId)];
          });
        }
      }

      // Handle ASK_SESSION_THREAD_SYNOPSIS - update Thread Summary in context panel
      if (event === AskRelayHubEvents.ASK_SESSION_THREAD_SYNOPSIS) {
        const synopsisFromParsed =
          typeof parsedRecord.synopsis === 'string'
            ? parsedRecord.synopsis
            : undefined;

        const synopsisData = parsedRecord.synopsisData;
        const askThreadSynopsisFromSynopsisData: string | undefined =
          typeof synopsisData === 'object' &&
          synopsisData !== null &&
          typeof (synopsisData as Record<string, unknown>).askThreadSynopsis ===
            'string'
            ? String(
                (synopsisData as Record<string, unknown>).askThreadSynopsis,
              )
            : undefined;

        const param1 = parsedRecord.param1;
        const askThreadSynopsisFromParam1: string | undefined =
          typeof param1 === 'object' && param1 !== null
            ? typeof (param1 as Record<string, unknown>).askThreadSynopsis ===
                'string'
              ? String(
                  (param1 as Record<string, unknown>).askThreadSynopsis,
                )
              : undefined
            : undefined;

        const synopsis =
          synopsisFromParsed ?? askThreadSynopsisFromSynopsisData ?? askThreadSynopsisFromParam1;

        if (synopsis) {
          setAskSession((prev) => {
            if (!prev) return prev;
            return { ...prev, askThreadSynopsis: synopsis };
          });
        }
      }
    },
    [
      currentUserId,
      setAskSession,
      setMessages,
      setProcessingStatus,
      setStreamingMessageId,
      displayedContentRef,
      lastStreamContentRef,
      toRevealRef,
    ],
  );

  const handleWorkspaceEventReceived = useCallback(
    async (
      event: string,
      _data: unknown,
      parsed?: Record<string, unknown>,
    ) => {
      const parsedRecord: Record<string, unknown> = parsed ?? {};

      if (event === WorkspaceRelayHubEvents.DATAROOMFILE_ADDED_TO_WORKSPACE) {
        const fileName =
          typeof parsedRecord.fileName === 'string' ? parsedRecord.fileName : undefined;

        if (uploadingFileNames.length > 0 && fileName) {
          const eventName = normalizeFileName(fileName);
          const hasMatch = uploadingFileNames.some(
            (uploadedName) => normalizeFileName(uploadedName) === eventName,
          );

          if (hasMatch) {
            setUploadingFileNames((prev) =>
              prev.filter((name) => normalizeFileName(name) !== eventName),
            );
          }
        }

        // Always refresh Ask session-scoped data for file-added events.
        // Upload name matching is only for clearing per-file upload indicators.
        await getWorkspaceData();
        if (sessionId) {
          await fetchAskSessionData();
        }
      } else if (
        event === WorkspaceRelayHubEvents.FILE_UPLOAD_ERRORED ||
        event === WorkspaceRelayHubEvents.URL_UPLOAD_ERRORED ||
        event === WorkspaceRelayHubEvents.CENTRAL_UPLOAD_ERRORED
      ) {
        const userMessage = parsedRecord.userMessage;
        const userMessageRecord =
          typeof userMessage === 'object' && userMessage !== null
            ? (userMessage as Record<string, unknown>)
            : undefined;

        const errorFileName =
          (typeof parsedRecord.fileName === 'string' ? parsedRecord.fileName : undefined) ??
          (userMessageRecord &&
          typeof userMessageRecord.fileName === 'string'
            ? userMessageRecord.fileName
            : undefined) ??
          (userMessageRecord &&
          typeof userMessageRecord.name === 'string'
            ? userMessageRecord.name
            : undefined) ??
          undefined;

        if (errorFileName && uploadingFileNames.length > 0) {
          const normalizedError = normalizeFileName(errorFileName);
          setUploadingFileNames((prev) =>
            prev.filter((name) => normalizeFileName(name) !== normalizedError),
          );
        } else {
          // Fallback: if we can't confidently match, clear all uploading state.
          setUploadingFileNames([]);
        }
      }
    },
    [
      uploadingFileNames,
      sessionId,
      fetchAskSessionData,
      getWorkspaceData,
      normalizeFileName,
      setUploadingFileNames,
    ],
  );

  return {
    handleAskEventReceived,
    handleWorkspaceEventReceived,
  };
};

